import { postJSON } from "../api.js";
import { esc } from "../ui/format.js";
import { uiCard, uiGet, uiKpis, uiMoney } from "../ui/kit.js";
import { toast } from "../ui/modals.js";
import { emLine } from "./empire.js";

/**
 * Time Machine (backend/timemachine.py), in the CEO Office: the whole
 * company on one timeline with the key decisions marked, the quarterly
 * checkpoints, and "what if?": branch from the checkpoint before any
 * decision. After a branch, the original road is drawn dashed behind yours.
 */

async function renderTimeMachineInto(host) {
  const t = await uiGet("/timemachine");
  const series = t.series.map((s) => ({ month: s.month, value: s.valuation }));
  const original = t.original && t.branch ? t.original.series.map((s) => ({ month: s.month, value: s.valuation })) : null;
  const now = series.length ? series[series.length - 1].value : 0;
  const theirs = original ? (original.find((s) => s.month === t.month) || original[original.length - 1]) : null;
  const delta = theirs ? now - theirs.value : null;
  host.innerHTML = `
    ${uiKpis([
      { label: "Months on this road", value: `${t.month}` },
      { label: "Key decisions", value: `${t.decisions.length}` },
      { label: "Checkpoints", value: `${t.checkpoints.length}`, sub: "founding + every quarter" },
      t.branch ? { label: `vs the old road at M${theirs ? theirs.month : t.month}`, value: `${delta >= 0 ? "+" : ""}${uiMoney(delta, { compact: true })}`, tone: delta >= 0 ? "pos" : "neg", sub: `branched from M${t.branch.from_month}` }
        : { label: "Branches", value: "0", sub: "you're on your first road" },
    ])}
    ${uiCard("Your company's life", emLine(series, { label: "This road", compare: original, compareLabel: "The road you left", height: 200, markers: t.decisions.slice(-14) }),
      { sub: t.branch ? `You left month ${t.branch.left_month} and went back to month ${t.branch.from_month}. Your old road is saved as "${t.branch.saved_slot}" (Save/Load).` : t.rules })}
    ${uiCard("What if?", t.decisions.length ? `<ol class="tm-list">${t.decisions.slice().reverse().map((d) => `
      <li><span class="tm-ic">${d.icon}</span><div><b>M${d.month} · ${esc(d.label)}</b>${d.detail ? ` <span>${esc(d.detail)}</span>` : ""}${d.amount ? ` <em>${uiMoney(d.amount, { compact: true })}</em>` : ""}</div>
        ${d.branch_from !== null && d.branch_from !== undefined ? `<button type="button" data-tm-branch="${d.branch_from}" data-tm-what="${esc(d.label)}">What if I hadn't? Go back to M${d.branch_from}</button>` : `<i>before the first checkpoint</i>`}</li>`).join("")}</ol>`
      : `<p class="sx-card-sub">No big decisions yet. Loans, launches, deals, the IPO and more will appear here.</p>`)}
    ${uiCard("Checkpoints", `<div class="cr-tm-row">${t.checkpoints.filter((m) => m < t.month).map((m) => `<button type="button" data-tm-branch="${m}">Month ${m}</button>`).join("") || "<span class=\"sx-card-sub\">The first quarterly checkpoint comes at month 3.</span>"}</div>`,
      { sub: "Jump to any checkpoint. Your current road is saved to a slot first, so you can always load it back." })}`;
  host.querySelectorAll("[data-tm-branch]").forEach((b) => b.addEventListener("click", async () => {
    const m = +b.dataset.tmBranch;
    const what = b.dataset.tmWhat ? ` (before “${b.dataset.tmWhat}”)` : "";
    if (!confirm(`Go back to month ${m}${what}? Your current road is saved to a slot first.`)) return;
    try {
      const r = await postJSON("/timemachine/branch", { month: m });
      toast(r.message);
      location.reload();
    } catch (e) { toast(e.message || "Couldn't travel back.", true); }
  }));
}

export { renderTimeMachineInto };
